import { useCallback, useEffect, useMemo, useState } from 'react';
import { Publication } from 'src/app/models/business/Publication';
import { getConfigCardIds } from '../../../api/paymentCards';
import {
  EMPTY_PUBLICATION_FORM,
  FORM_STEPS,
  PIPE_COMPANY_NAME_TO_ID,
  PIPE_ORIGIN_NAME_TO_ID,
} from '../constants';
import { PublicationFormValues, PublicationMarketplace } from '../types';

const normalizeStatus = (publication: Publication): string => {
  if (publication.status) {
    const value = publication.status.toLowerCase();
    if (value === 'active' || value === 'activa') return 'activa';
    if (value === 'inactive' || value === 'inactiva') return 'inactiva';
  }
  if (publication.publication_state_id === '1') return 'activa';
  if (publication.publication_state_id === '2') return 'inactiva';
  return 'activa';
};

const toStringValue = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return '';
  return String(value);
};

/**
 * Mapea una publicación existente a los valores del formulario para edición.
 * Los campos que no provee el modelo actual quedan vacíos / con default.
 */
const mapCompanyToForm = (publication: Publication): string => {
  if (publication.company_id) return String(publication.company_id);
  const companyName = publication.company || publication.company_name;
  if (companyName && PIPE_COMPANY_NAME_TO_ID[companyName]) {
    return PIPE_COMPANY_NAME_TO_ID[companyName];
  }
  return companyName || '';
};

const mapOriginToForm = (publication: Publication): string => {
  if (publication.origin_id !== undefined && publication.origin_id !== null && publication.origin_id !== '') {
    return String(publication.origin_id);
  }
  const originName = publication.origin_name;
  if (originName && PIPE_ORIGIN_NAME_TO_ID[originName]) {
    return PIPE_ORIGIN_NAME_TO_ID[originName];
  }
  return '';
};

const mapPublicationToForm = (publication: Publication): PublicationFormValues => ({
  ...EMPTY_PUBLICATION_FORM,
  publicationId: publication.publication_id || '',
  productId: publication.product_id ? String(publication.product_id) : '',
  sku: publication.sku || '',
  productName: publication.name || publication.product_name || '',
  category: publication.category || publication.category_name || '',
  company: mapCompanyToForm(publication),
  origin: mapOriginToForm(publication),
  currency: publication.currency || 'ARS',
  status: normalizeStatus(publication),
  relevance: toStringValue(publication.relevance),
  pvpPrice: toStringValue(publication.pvp_price ?? publication.PrecioPVP ?? publication.price),
  pvCampaignPrice: toStringValue(publication.price),
  campaigns: publication.campaigns_id?.map(String) ?? [],
  zones: publication.zones_id ?? [],
  targets: publication.targets_id?.map(String) ?? [],
  stockQty: toStringValue(publication.quantity),
  alarmBase: toStringValue(publication.base),
  alarmMin: toStringValue(publication.minimum),
  oneePaymentCards: getConfigCardIds(publication.config_values as Record<string, unknown> | undefined),
});

interface UsePublicationFormParams {
  open: boolean;
  publication?: Publication | null;
  marketplace: PublicationMarketplace;
}

export const usePublicationForm = ({ open, publication, marketplace }: UsePublicationFormParams) => {
  const isEditing = Boolean(publication && publication.publication_id);
  const [values, setValues] = useState<PublicationFormValues>(EMPTY_PUBLICATION_FORM);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!open) return;
    setCurrentStep(0);
    setValues(publication ? mapPublicationToForm(publication) : EMPTY_PUBLICATION_FORM);
  }, [open, publication]);

  const onChange = useCallback(
    <K extends keyof PublicationFormValues>(field: K, value: PublicationFormValues[K]) => {
      setValues((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const isValid = useMemo(() => {
    const hasSku = isEditing || Boolean(values.sku);
    const hasPvp = Boolean(values.pvpPrice);
    if (marketplace === 'enova') {
      return hasSku && hasPvp;
    }
    return hasSku && hasPvp && Boolean(values.company) && Boolean(values.origin);
  }, [isEditing, marketplace, values.sku, values.pvpPrice, values.company, values.origin]);

  const goToStep = useCallback((step: number) => {
    const clamped = Math.max(0, Math.min(step, FORM_STEPS.length - 1));
    setCurrentStep(clamped);
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, FORM_STEPS.length - 1));
  }, []);

  const goBack = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  return {
    values,
    onChange,
    isEditing,
    isValid,
    currentStep,
    totalSteps: FORM_STEPS.length,
    goToStep,
    goNext,
    goBack,
  };
};
